import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FiltersIcon } from "./Icons.js";
import { ChipMedallion } from "./ChipMedallion.js";
import { Button } from "../ui/Button.js";
import "./FilterRadioPopover.css";

const FilterRadioPopover = ({
  sections = [],
  footerAction = null,
  ariaLabel = "Filters",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const nonEmptySections = sections.filter(
    (sec) => Array.isArray(sec.options) && sec.options.length > 0,
  );

  return (
    <div className="filter-radio-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`filter-radio-trigger ${isOpen ? "is-open" : ""}`.trim()}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <FiltersIcon size="sm" />
      </button>

      {isOpen && (
        <dialog
          open
          className="filter-radio-popover"
          aria-label={`${ariaLabel} options`}
        >
          {nonEmptySections.map((section) => (
            <div
              key={section.id}
              className="filter-radio-section"
              role="radiogroup"
              aria-label={section.label}
            >
              <h4 className="filter-radio-heading">{section.label}</h4>
              <div className="filter-radio-options">
                {section.options.map((opt, idx) => {
                  const isSelected =
                    String(section.selectedValue) === String(opt.value);
                  return (
                    <button
                      key={`${opt.value}-${idx}`}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`filter-radio-option ${isSelected ? "is-selected" : ""}`.trim()}
                      onClick={() => {
                        section.onSelect(opt.value);
                      }}
                    >
                      <span className="filter-radio-circle" aria-hidden="true">
                        {isSelected && <span className="filter-radio-dot" />}
                      </span>
                      <span className="filter-radio-label">
                        {opt.showMedallion && (
                          <ChipMedallion label={opt.label} />
                        )}
                        <span>{opt.label}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {footerAction && (
            <div className="filter-radio-footer">
              <Button
                variant="primary"
                type="button"
                className="filter-radio-footer-btn"
                onClick={() => {
                  setIsOpen(false);
                  footerAction.onClick();
                }}
              >
                {footerAction.label}
              </Button>
            </div>
          )}
        </dialog>
      )}
    </div>
  );
};

FilterRadioPopover.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          label: PropTypes.string.isRequired,
          showMedallion: PropTypes.bool,
        }),
      ).isRequired,
      selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      onSelect: PropTypes.func.isRequired,
    }),
  ),
  footerAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  ariaLabel: PropTypes.string,
};

export default FilterRadioPopover;
